import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { editProfile } from "../redux/slices/profileSlice";
import { fetchUserDetail } from "../redux/slices/userSlice";
import Button from "./common/button";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import BackdropSpinner from "./backdropSpinner";

const Profile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, loading2 } = useSelector((state) => state.user);
  const { loading } = useSelector((state) => state.profile);
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // State to store user payment details
  const [formData, setFormData] = useState({
    account_name: "",
    account_number: "",
    confirm_account_number: "",
    ifsc_code: "",
    upi_id: "",
    confirm_upi_id: "",
  });

  // Retrieve user ID from local storage
  const userId = localStorage.getItem("userID");

  // Fetch user details when component mounts
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserDetail(userId));
    }
  }, [dispatch, userId]);

  const userData = user?.data?.[0];

  // Populate form fields with existing user data when available
  useEffect(() => {
    if (userData) {
      setFormData({
        account_name: userData.account_name || "",
        account_number: userData.account_number || "",
        confirm_account_number: userData.account_number || "",
        confirm_upi_id: userData.upi_id || "",
        ifsc_code: userData.ifsc_code || "",
        upi_id: userData.upi_id || "",
      });
    }
  }, [userData]);

  // Handle form input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If the field is a text field (like account_name or UPI id), apply space validation
    if (
      name === "account_name" ||
      name === "upi_id" ||
      name === "account_number"
    ) {
      // Check for leading spaces
      if (value.startsWith(" ")) {
        setErrors({
          ...errors,
          [name]: t("profile.validation.no_leading_space"),
        });
        return;
      }

      // Check for multiple spaces in the middle
      if (/\s{2,}/.test(value)) {
        setErrors({
          ...errors,
          [name]: t("profile.validation.no_multiple_spaces"),
        });
        return;
      }

      // Remove leading/trailing spaces and update formData
      setFormData({ ...formData, [name]: value.trim() });
    }

    // For numeric fields (like account_number), allow only digits
    if (name === "account_number" || name === "confirm_account_number") {
      if (!/^\d*$/.test(value)) {
        setErrors({
          ...errors,
          [name]: t("profile.validation.only_numbers_allowed"),
        });
        return;
      }
    }

    // Clear errors and update state
    setErrors({ ...errors, [name]: "" });
    setFormData({ ...formData, [name]: value });
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    if (paymentMethod === "bank") {
      if (!formData.account_name)
        newErrors.account_name = t("profile.validation.account_name_required");
      if (!formData.account_number)
        newErrors.account_number = t(
          "profile.validation.account_number_required"
        );
      if (formData.account_number !== formData.confirm_account_number)
        newErrors.confirm_account_number = t(
          "profile.validation.account_number_mismatch"
        );
      if (!formData.ifsc_code)
        newErrors.ifsc_code = t("profile.validation.ifsc_required");
    } else {
      if (!formData.upi_id)
        newErrors.upi_id = t("profile.validation.upi_id_required");
      if (formData.upi_id !== formData.confirm_upi_id)
        newErrors.confirm_upi_id = t("profile.validation.upi_id_mismatch");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return; // Stop if validation fails

    const profileData = {
      user_id: userId,
      account_name: formData.account_name,
      account_number: formData.account_number,
      ifsc_code: formData.ifsc_code,
      upi_id: formData.upi_id,
    };

    try {
      // Dispatch edit profile action
      await dispatch(editProfile({ profileData })).unwrap();

      // Show success message
      toast.success(t("profile.profile_updated"));

      // Refetch user details
      dispatch(fetchUserDetail(userId));

      // Exit editing mode
      setIsEditing(false);
    } catch (error) {
      // Handle potential errors
      toast.error(t("profile.profile_update_failed"));
      console.error("Error updating profile:", error);
    }
  };

  // Enable edit mode
  const handleEdit = () => {
    setIsEditing(true); // Enable editing
  };

  // Exit edit mode without saving changes
  const handleEditClose = () => {
    setIsEditing(false);
  };

  // Show loader while fetching user data
  if (loading || loading2) {
    return <BackdropSpinner />;
  }

  return (
    <>
      {/* Profile Title Section */}
      <div className="flex justify-center my-3 flex-row gap-5">
        <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
          {t("profile.title")}
        </p>
      </div>

      {/* Profile Card Container */}
      <div className="flex justify-center items-center text-white">
        <div className="p-6 w-full max-w-lg sm:max-w-md rounded-lg shadow-md">
          {/* User Avatar Section */}
          <div className="flex justify-center mb-6 relative">
            <div className="w-24 h-24 bg-gradient-to-br rounded-full border-4 border-[#BB9A3E] shadow-xl p-1 overflow-hidden hover:scale-105 transform transition-transform duration-300">
              <img
                src="/images/avatar.avif"
                alt="User Avatar"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </div>

          {/* User Details Section */}
          <div className="rounded-lg p-4 shadow-inner mb-6 bg-gray-800">
            {/* Username */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold uppercase">
                {t("profile.name")}
              </h3>
              <p className="text-sm font-medium">
                {user?.data?.[0].username || "N/A"}
              </p>
            </div>
            {/* Phone Number */}
            <div>
              <h3 className="text-sm font-semibold uppercase">
                {t("profile.phone_number")}
              </h3>
              <p className="text-sm font-medium">
                {user?.data?.[0].phone_number || "N/A"}
              </p>
            </div>
          </div>

          {/* Payment Method Section */}
          <div className="mb-4">
            <div className="flex flex-row justify-between">
              <h3 className="text-lg font-semibold mb-2">
                {t("profile.payment_method")}
              </h3>
              {/* Edit Button */}
              <div className="text-right mb-4">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="text-lg text-[#f3da76] hover:underline "
                  >
                    <FaEdit />
                  </button>
                ) : (
                  <button
                    onClick={handleEditClose}
                    className="text-lg text-red-500 hover:underline"
                  >
                    <IoMdRemoveCircleOutline />
                  </button>
                )}
              </div>
            </div>

            {/* Payment Method Options (Bank / UPI) */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="bank"
                  checked={paymentMethod === "bank"}
                  onChange={() => setPaymentMethod("bank")}
                  className="mr-2 bg-amber-300"
                />
                <span className="text-sm">{t("profile.using_bank")}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="upi"
                  checked={paymentMethod === "upi"}
                  onChange={() => setPaymentMethod("upi")}
                  className="mr-2"
                />
                <span className="text-sm">{t("profile.using_upi")}</span>
              </label>
            </div>
          </div>

          {/* Payment Details Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {paymentMethod === "bank" ? (
              /* Bank Account Details */
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.account_holder_name")}</span>
                    <input
                      type="text"
                      name="account_name"
                      value={formData.account_name}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing}
                    />
                    {errors.account_name && (
                      <p className="text-red-500 text-xs">
                        {errors.account_name}
                      </p>
                    )}
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.account_number")}</span>
                    <input
                      type="number"
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing }
                    />
                    {errors.account_number && (
                      <p className="text-red-500 text-xs">
                        {errors.account_number}
                      </p>
                    )}
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.confirm_account_number")}</span>
                    <input
                      type="number"
                      name="confirm_account_number"
                      value={formData.confirm_account_number}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing }
                    />
                    {errors.confirm_account_number && (
                      <p className="text-red-500 text-xs">
                        {errors.confirm_account_number}
                      </p>
                    )}
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.ifsc_code")}</span>
                    <input
                      type="text"
                      name="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing}
                    />
                    {errors.ifsc_code && (
                      <p className="text-red-500 text-xs">{errors.ifsc_code}</p>
                    )}
                  </label>
                </div>
              </>
            ) : (
              /* UPI Payment Details */
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.upi_id")}</span>
                    <input
                      type="text"
                      name="upi_id"
                      value={formData.upi_id}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing }
                    />
                    {errors.upi_id && (
                      <p className="text-red-500 text-xs">{errors.upi_id}</p>
                    )}
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span>{t("profile.confirm_upi_id")}</span>
                    <input
                      type="text"
                      name="confirm_upi_id"
                      value={formData.confirm_upi_id}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1 border ${
                        !isEditing 
                          ? "border-gray-500 text-gray-500"
                          : "border-[#f3da76]"
                      } rounded-md text-sm bg-transparent`}
                      disabled={!isEditing }
                    />
                    {errors.confirm_upi_id && (
                      <p className="text-red-500 text-xs">
                        {errors.confirm_upi_id}
                      </p>
                    )}
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                className="mr-2"
                disabled={!isEditing }
              />
              <span className="text-sm">
                {t("profile.confirmation_checkbox")}
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleSubmit}
              disabled={!isConfirmed || loading || !isEditing}
              text={loading ? t("loading") : t("profile.confirm_button")}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
